import React, { useState } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Palette, Target, FileText, Users, FolderOpen, 
  BarChart3, Calendar, Settings, Home, Sparkles, 
  Zap, Plus, Bell, Search, Sun, Moon
} from 'lucide-react';
import CreativeHub from './components/CreativeHub';
import Campaigns from './components/Campaigns';
import Requests from './components/Requests';
import Collaboration from './components/Collaboration';
import FileLibrary from './components/FileLibrary';
import Analytics from './components/Analytics';
import CalendarComponent from './components/Calendar';
import SettingsComponent from './components/Settings';

const CreativesDashboard = () => {
  const location = useLocation();
  const [hoveredTab, setHoveredTab] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(true);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const navigationItems = [
    { 
      path: '', 
      label: 'Creative Hub', 
      icon: Home,
      description: 'Overview & Insights',
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-500'
    },
    { 
      path: 'campaigns', 
      label: 'Campaigns', 
      icon: Target,
      description: 'Marketing Campaigns',
      color: 'from-purple-500 to-pink-500',
      bgColor: 'bg-purple-500'
    },
    { 
      path: 'analytics', 
      label: 'Analytics', 
      icon: BarChart3,
      description: 'Performance Data',
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-500'
    },
    { 
      path: 'requests', 
      label: 'Requests', 
      icon: FileText,
      description: 'Design Requests',
      color: 'from-emerald-500 to-teal-500',
      bgColor: 'bg-emerald-500'
    },
    { 
      path: 'collaboration', 
      label: 'Collaboration', 
      icon: Users,
      description: 'Team Communication',
      color: 'from-orange-500 to-red-500',
      bgColor: 'bg-orange-500'
    },
    { 
      path: 'files', 
      label: 'File Library', 
      icon: FolderOpen,
      description: 'Asset Management',
      color: 'from-indigo-500 to-purple-500',
      bgColor: 'bg-indigo-500'
    },
    { 
      path: 'calendar', 
      label: 'Calendar', 
      icon: Calendar,
      description: 'Schedule & Events',
      color: 'from-pink-500 to-rose-500',
      bgColor: 'bg-pink-500'
    },
    { 
      path: 'settings', 
      label: 'Settings', 
      icon: Settings,
      description: 'Configuration',
      color: 'from-gray-500 to-slate-500',
      bgColor: 'bg-gray-500'
    },
  ];

  const isActiveRoute = (path) => {
    if (path === '') {
      return location.pathname === '/creatives' || location.pathname === '/creatives/';
    }
    return location.pathname === `/creatives/${path}`;
  };

  return (
    <div className={`min-h-screen relative transition-all duration-500 ${
      isDarkMode 
        ? 'bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900' 
        : 'bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50'
    }`}>
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-20 left-10 w-96 h-96 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob ${
          isDarkMode ? 'bg-purple-500/20' : 'bg-purple-300/30'
        }`}></div>
        <div className={`absolute top-40 right-10 w-96 h-96 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000 ${
          isDarkMode ? 'bg-pink-500/20' : 'bg-pink-300/30'
        }`}></div>
        <div className={`absolute -bottom-8 left-20 w-96 h-96 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000 ${
          isDarkMode ? 'bg-blue-500/20' : 'bg-blue-300/30'
        }`}></div>
        <div className={`absolute inset-0 ${
          isDarkMode 
            ? 'bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent)]' 
            : 'bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.8),transparent)]'
        }`}></div>
      </div>

      {/* Full-Screen Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`relative sticky top-0 z-30 border-b transition-all duration-500 ${
          isDarkMode 
            ? 'bg-black/20 backdrop-blur-xl border-white/10' 
            : 'bg-white/80 backdrop-blur-xl border-gray-200/50'
        }`}
      >
        <div className="w-full px-8 py-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-6">
              <motion.div 
                className="p-4 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 rounded-2xl shadow-2xl"
                whileHover={{ scale: 1.05, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <Palette className="h-8 w-8 text-white" />
              </motion.div>
              <div>
                <h1 className={`text-3xl font-bold bg-gradient-to-r bg-clip-text text-transparent ${
                  isDarkMode 
                    ? 'from-white to-purple-200' 
                    : 'from-gray-900 to-purple-600'
                }`}>
                  Creatives Studio
                </h1>
                <p className={`text-sm flex items-center space-x-2 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  <Sparkles className="h-4 w-4 text-yellow-400" />
                  <span>Next-Generation Marketing & Collaboration Hub</span>
                </p>
              </div>
            </div>
            
            {/* Header Actions */}
            <div className="flex items-center space-x-4">
              <button className={`p-3 rounded-xl hover:transition-all duration-300 ${
                isDarkMode 
                  ? 'bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}>
                <Search className="h-5 w-5" />
              </button>
              <button className={`p-3 rounded-xl hover:transition-all duration-300 relative ${
                isDarkMode 
                  ? 'bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}>
                <Bell className="h-5 w-5" />
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
              </button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleTheme}
                className={`p-3 rounded-xl font-medium hover:shadow-lg transition-all duration-300 flex items-center space-x-2 ${
                  isDarkMode 
                    ? 'bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white' 
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                {isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-medium hover:shadow-lg transition-all duration-300 flex items-center space-x-2"
              >
                <Plus className="h-5 w-5" />
                <span>New Project</span>
              </motion.button>
            </div>
          </div>
          
          {/* Centered Navigation Tabs - No Horizontal Scrolling */}
          <div className="flex justify-center">
            <div className="flex space-x-1 max-w-full">
              {navigationItems.map((item, index) => {
                const Icon = item.icon;
                const isActive = isActiveRoute(item.path);
                const isHovered = hoveredTab === index;
                
                return (
                  <motion.div
                    key={item.path}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onHoverStart={() => setHoveredTab(index)}
                    onHoverEnd={() => setHoveredTab(null)}
                  >
                    <Link
                      to={item.path}
                      className={`group relative flex flex-col items-center space-y-2 px-4 py-3 rounded-2xl text-sm font-medium transition-all duration-300 ${
                        isActive
                          ? 'bg-gradient-to-r ' + item.color + ' text-white shadow-2xl scale-105'
                          : isDarkMode
                            ? 'text-gray-300 hover:text-white hover:bg-white/10 hover:shadow-xl'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-white/50 hover:shadow-xl'
                      }`}
                    >
                      {/* Background Glow Effect */}
                      {isActive && (
                        <motion.div
                          className={`absolute inset-0 bg-gradient-to-r ${item.color} rounded-2xl opacity-30 blur-xl`}
                          animate={{ scale: [1, 1.1, 1] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        />
                      )}
                      
                      {/* Icon Container */}
                      <div className={`relative p-2 rounded-xl transition-all duration-300 ${
                        isActive 
                          ? 'bg-white/20 backdrop-blur-sm' 
                          : isDarkMode
                            ? 'bg-white/5 group-hover:bg-white/10'
                            : 'bg-gray-100 group-hover:bg-gray-200'
                      }`}>
                        <Icon className={`h-5 w-5 transition-all duration-300 ${
                          isActive ? 'text-white' : isDarkMode ? 'text-gray-300 group-hover:text-white' : 'text-gray-600 group-hover:text-gray-900'
                        }`} />
                        
                        {/* Hover Animation */}
                        {isHovered && !isActive && (
                          <motion.div
                            className={`absolute inset-0 rounded-xl ${
                              isDarkMode 
                                ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20' 
                                : 'bg-gradient-to-r from-purple-100/50 to-pink-100/50'
                            }`}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                          />
                        )}
                      </div>
                      
                      {/* Label */}
                      <span className="font-semibold text-xs">{item.label}</span>
                      
                      {/* Description Tooltip */}
                      <AnimatePresence>
                        {isHovered && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            className={`absolute -bottom-12 left-1/2 transform -translate-x-1/2 px-3 py-1 backdrop-blur-sm text-xs rounded-lg whitespace-nowrap z-50 border ${
                              isDarkMode 
                                ? 'bg-black/90 text-white border-white/20' 
                                : 'bg-white/90 text-gray-900 border-gray-200'
                            }`}
                          >
                            {item.description}
                            <div className={`absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1 w-2 h-2 border-l border-t rotate-45 ${
                              isDarkMode 
                                ? 'bg-black/90 border-white/20' 
                                : 'bg-white/90 border-gray-200'
                            }`}></div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Full-Screen Content Area - No Padding Constraints */}
      <div className="relative w-full min-h-[calc(100vh-200px)]">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="w-full h-full"
          >
            <Routes>
              <Route path="" element={<CreativeHub isDarkMode={isDarkMode} />} />
              <Route path="campaigns" element={<Campaigns isDarkMode={isDarkMode} />} />
              <Route path="requests" element={<Requests isDarkMode={isDarkMode} />} />
              <Route path="collaboration" element={<Collaboration isDarkMode={isDarkMode} />} />
              <Route path="files" element={<FileLibrary isDarkMode={isDarkMode} />} />
              <Route path="analytics" element={<Analytics isDarkMode={isDarkMode} />} />
              <Route path="calendar" element={<CalendarComponent isDarkMode={isDarkMode} />} />
              <Route path="settings" element={<SettingsComponent isDarkMode={isDarkMode} />} />
            </Routes>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Floating Action Button */}
      <motion.div
        className="fixed bottom-8 right-8 z-30"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.5, type: "spring" }}
      >
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="p-5 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300"
        >
          <Zap className="h-7 w-7" />
        </motion.button>
      </motion.div>
    </div>
  );
};

export default CreativesDashboard; 